import { Stack } from '@mui/material';
import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { FormFieldWrapper } from '@procureai/ui-form-mui';
import { CompetitiveBiddingConfigForm } from '@procureai/ui-modules-mui';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { AstronautUserAutocomplete } from '~/components/AstronautUserAutocomplete';
import { useFormFieldErrorTranslation } from '~/hooks';

import { DeadlineInput } from './components/DeadlineInput/DeadlineInput';

export interface DemandDetailFormProps {
  capabilityIds: string[];
  commodityGroupIds: string[];
  referenceDisabled: boolean;
  awardingRuleChangeDisabled?: boolean;
  competitiveBiddingChangeDisabled?: boolean;
}

export const DemandDetailForm = ({
  capabilityIds,
  commodityGroupIds,
  competitiveBiddingChangeDisabled,
}: DemandDetailFormProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandDetailWidget' });
  const { errorToHelperText } = useFormFieldErrorTranslation();

  return (
    <Stack spacing={4}>
      <FormFieldWrapper label={t('responsibles')}>
        <Controller
          name="responsible"
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <AstronautUserAutocomplete
              fullWidth={true}
              {...field}
              TextFieldProps={{
                error: !!error,
                helperText: errorToHelperText(error),
              }}
              capabilityIds={capabilityIds}
              commodityGroupIds={commodityGroupIds}
            />
          )}
        />
      </FormFieldWrapper>
      <FormFieldWrapper label={t('capabilities')}>
        <Controller
          name="capabilities"
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <CuriosityAutocomplete
              ns={CuriosityNamespaceType.CAPABILITY}
              multiple
              fullWidth={true}
              simplifyValue
              {...field}
              TextFieldProps={{
                error: !!error,
                helperText: errorToHelperText(error),
              }}
            />
          )}
        />
      </FormFieldWrapper>
      <CompetitiveBiddingConfigForm
        disabled={competitiveBiddingChangeDisabled}
        pathPrefix={['competitiveBidding']}
        deadlineInput={
          <Controller
            name="competitiveBidding.deadline"
            render={({ field, fieldState: { error } }) => (
              <FormFieldWrapper label={t('competitiveBidding.deadline')}>
                <DeadlineInput
                  sx={{ marginTop: 1 }}
                  {...field}
                  disabled={competitiveBiddingChangeDisabled}
                  value={field.value ?? null}
                  error={error}
                />
              </FormFieldWrapper>
            )}
          />
        }
      />
    </Stack>
  );
};
